import { registerDestructor } from '@ember/destroyable';
import Component from '@glimmer/component';
import { restartableTask, timeout } from 'ember-concurrency';

export interface PollingOptions {
  /**
   * Delay between polls in seconds (default: 5)
   */
  refreshDelayInSeconds?: number;
}

/**
 * Abstract base class to add polling functionality to a component.
 */
export abstract class PollingComponent<T> extends Component<T> {
  /**
   * Override this property to control when polling should continue
   */
  protected abstract get shouldPoll(): boolean;

  /**
   * Override this method to define what happens on each poll
   */
  protected abstract performPoll(): Promise<void>;

  /**
   * Polling configuration options
   */
  protected pollingOptions: PollingOptions = {
    refreshDelayInSeconds: 5,
  };

  constructor(owner: unknown, args: unknown) {
    super(owner, args);

    void this.pollTask.perform();

    registerDestructor(this, () => {
      this.stopPolling();
    });
  }

  /**
   * Main polling task that runs continuously
   */
  pollTask = restartableTask(async () => {
    const { refreshDelayInSeconds = 5 } = this.pollingOptions;

    if (!this.shouldPoll) return;

    await this.refreshTask.perform();

    // Stop polling if condition is no longer met
    if (!this.shouldPoll) return;

    await timeout(refreshDelayInSeconds * 1000);
    void this.pollTask.linked().perform();
  });

  /**
   * Task that executes the actual polling logic
   */
  refreshTask = restartableTask(async () => {
    await this.performPoll();
  });

  /**
   * Manually trigger a single poll
   */
  protected async triggerPoll(): Promise<void> {
    await this.refreshTask.perform();
  }

  /**
   * Stop all polling tasks
   */
  protected stopPolling(): void {
    void this.pollTask.cancelAll();
    void this.refreshTask.cancelAll();
  }

  /**
   * Restart polling from the beginning
   */
  protected restartPolling(): void {
    void this.pollTask.perform();
  }
}
